/***
 *
 *   SIGN UP (user)
 *   Signup form for child users
 *
 **********/

import React, { useContext, useState } from 'react';
import { useLocation, AuthContext, ViewContext } from 'components/lib';
import Styles from './user.module.scss'

import {
	TextField,
	Typography,
	IconButton,
	InputAdornment,
	LinearProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { useNavigate } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import axios from 'axios';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';


// Password strength assessment
const getPasswordStrength = (password) => {
	if (!password) return 0;

	let strength = 0;

	if (password.length > 0) strength += 10
	// Length check
	if (password.length >= 8) strength += 15;

	// Contains uppercase
	if (/[A-Z]/.test(password) && password.length >= 8) strength += 25;

	// Contains number
	if (/\d/.test(password) && password.length >= 8) strength += 25;

	// Contains special character
	if (/[^A-Za-z0-9]/.test(password) && password.length >= 8) strength += 25;

	return strength;
};

const getStrengthLabel = (strength) => {
	if (strength === 0) return 'No Password';
	if (strength <= 15) return 'Too Weak';
	if (strength <= 25) return 'Weak';
	if (strength <= 50) return 'Fair';
	if (strength <= 75) return 'Good';
	return 'Strong';
};

const getStrengthColor = (strength) => {
	if (strength <= 25) return '#f44336'; // red
	if (strength <= 50) return '#ff9800'; // orange
	if (strength <= 75) return '#2196f3'; // blue
	return '#4caf50'; // green
};

export function SignupUser(props) {
	const context = useContext(AuthContext);
	const viewContext = useContext(ViewContext);

	const navigate = useNavigate();
	const { search } = useLocation();
	const queryParams = new URLSearchParams(search);

	const id = queryParams.get('id');
	const email = queryParams.get('email');

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [touched, setTouched] = useState({});
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const [formValues, setFormValues] = useState({
		firstName: '',
		email: email || '',
		password: '',
		confirmPassword: '',
		inviteId: id || ''
	});

	const passwordStrength = getPasswordStrength(formValues.password);
	const strengthLabel = getStrengthLabel(passwordStrength);
	const strengthColor = getStrengthColor(passwordStrength);

	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Valid email
	const nameRegex = /^[A-Za-z]+$/; // Only letters

	const validate = () => {
		const newErrors = {};

		// First name: required + only letters
		if (!formValues.firstName?.trim()) {
			newErrors.firstName = 'First name is required';
		} else if (!nameRegex.test(formValues.firstName)) {
			newErrors.firstName = 'First name can only contain letters';
		} else if (formValues.firstName?.trim().length > 30) {
			newErrors.firstName = 'First name cannot exceed 30 characters.'
		}

		if (!emailRegex.test(formValues.email)) {
			newErrors.email = 'Enter a valid email address';
		}

		if (!formValues.password || formValues.password.length < 8) {
			newErrors.password = 'Password must be at least 8 characters long';
		} else if (!/[A-Z]/.test(formValues.password)) {
			newErrors.password = 'Password must contain at least one uppercase letter';
		} else if (!/[a-z]/.test(formValues.password)) {
			newErrors.password = 'Password must contain at least one lowercase letter';
		} else if (!/\d/.test(formValues.password)) {
			newErrors.password = 'Password must contain at least one number';
		} else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formValues.password)) {
			newErrors.password = 'Password must contain at least one special character';
		}

		if (formValues.password !== formValues.confirmPassword) {
			newErrors.confirmPassword = 'Passwords do not match';
		}

		return newErrors;
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormValues((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleBlur = (field) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		setErrors(validate());
	};


	const handleSubmit = () => {
		const formErrors = validate();
		setErrors(formErrors);

		setTouched({
			firstName: true,
			email: true,
			password: true,
			confirmPassword: true,
		});

		if (Object.keys(formErrors).length !== 0) return;

		setLoading(true);

		const options = {
			method: 'POST',
			url: '/api/user',
			data: {
				first_name: formValues.firstName,
				email: formValues.email,
				password: formValues.password,
				confirm_password: formValues.confirmPassword,
				invite_id: formValues.inviteId,
			},
		};

		axios
			.request(options)
			.then(function (response) {
				if (response.data) {
					(response.data['2fa_required'] ? navigate(`/signin/otp?token=${response.data.token}`) : context.signin(response))
				} else {
					setLoading(false);
					viewContext.notification.show(response.data.message, 'error');
				}
			})
			.catch((error) => {
				setLoading(false);
				const message = error.response?.data?.message || error.response?.data?.error || error.repsonse?.data;
				viewContext.notification.show(message, 'error');
			})
	}

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSubmit();
		}
	};


	return (
		<>
			{loading ? (
				<LoadingBackdrop isOpen={loading} />
			) : (
				<div id='mainContainer' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
					<div id='formContainer' className={Styles.formContainer}>
						<Typography
							variant="h5"
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginBottom: '24px',
								fontWeight: 600,
								color: '#1450a0'
							}}
						>
							<CorporateFareIcon sx={{ marginRight: '8px' }} />
							Signup with Organization
						</Typography>
						<Typography
							variant="subtitle1"
							sx={{
								marginTop: '4px',
								marginBottom: '16px',
								color: 'text.secondary',
								textAlign: 'center'
							}}
						>
							Join your Organization by creating an account with your email.
						</Typography>

						<div id='nameInputContainer' className={Styles.formFieldContainer}>
							<TextField
								label="First Name"
								type="text"
								name="firstName"
								value={formValues.firstName}
								onChange={handleChange}
								onBlur={() => handleBlur('firstName')}
								onKeyDown={handleKeyDown}
								error={touched.firstName && !!errors.firstName}
								helperText={touched.firstName && errors.firstName}
								required
								fullWidth
							/>
						</div>
						<div id='emailInputContainer' className={Styles.formFieldContainer}>
							<TextField
								label="Email"
								type="email"
								name="email"
								value={formValues.email}
								onChange={handleChange}
								onBlur={() => handleBlur('email')}
								onKeyDown={handleKeyDown}
								error={touched.email && !!errors.email}
								helperText={touched.email && errors.email}
								required
								fullWidth
								disabled
							/>
						</div>

						<div id='passwordInputContainer' className={Styles.formFieldContainer}>
							<TextField
								label="Password"
								type={showPassword ? 'text' : 'password'}
								name="password"
								value={formValues.password || ''}
								onChange={handleChange}
								onBlur={() => handleBlur('password')}
								onKeyDown={handleKeyDown}
								error={touched.password && !!errors.password}
								helperText={touched.password && errors.password ? errors.password : 'Must be at least 8 characters, include uppercase, number, and special character'}
								required
								fullWidth
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => { setShowPassword(!showPassword) }}
												edge="end"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>

							{formValues.password && (
								<div id='passwordStrengthContainer' className={Styles.passwordStrengthContainer}>
									<div id='passwordStrength' className={Styles.passwordStrength}>
										<Typography variant="caption">Password Strength: {strengthLabel}</Typography>
										<Typography variant="caption" style={{ color: strengthColor }}>{passwordStrength}%</Typography>
									</div>
									<LinearProgress
										variant="determinate"
										value={passwordStrength}
										sx={{
											height: 6,
											borderRadius: 3,
											backgroundColor: 'rgba(0,0,0,0.08)',
											'& .MuiLinearProgress-bar': {
												backgroundColor: strengthColor,
											}
										}}
									/>
								</div>
							)}
						</div>

						<div id='confirmPassowordInputContainer' className={Styles.formFieldContainer}>
							<TextField
								label="Confirm Password"
								type={showConfirmPassword ? 'text' : 'password'}
								name="confirmPassword"
								value={formValues.confirmPassword || ''}
								onChange={handleChange}
								onBlur={() => handleBlur('confirmPassword')}
								onKeyDown={handleKeyDown}
								error={touched.confirmPassword && !!errors.confirmPassword}
								helperText={touched.confirmPassword && errors.confirmPassword}
								required
								fullWidth
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle confirm password visibility"
												onClick={() => { setShowConfirmPassword(!showConfirmPassword) }}
												edge="end"
											>
												{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</div>

						<AivagamButton
							id="signupEmailButton"
							buttonText="Create Account"
							onClick={handleSubmit}
							classStyles={{
								width: "50%",
								fontSize: "1em",
								padding: "0.5em",
								marginBottom: "0.6em",
								boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
							}}
							type="confirm"
							disabled={loading}
						/>
						<div id='signInLink' className={Styles.signInLink}>
							Already Registered?
							<p
								onClick={() => {
									navigate('/signin')
								}}
								style={{ color: 'blue', cursor: 'pointer', display: 'inline', textDecoration: 'underline' }}>
								Sign In
							</p>
						</div>
					</div>
				</div >
			)}
		</>
	);
}
